import { useState, useMemo, useEffect } from "react";
import dayjs, { Dayjs } from "dayjs";
import InvoiceCard from "./components/InvoiceCard";
import { useInvoices } from "@/hooks/useInvoices";
import QueryStateWrapper from "@/components/ui/QueryStateWrapper";
import FilterBar from "@/components/ui/FilterBar";
import { useNavigate } from "react-router-dom";
import { useCurrentSession } from "@/hooks/useSession";
import { toast } from "react-toastify";

const PAGE_SIZE = 10;

const InvoicesPage = () => {
  const navigate = useNavigate();

  // فحص حالة الجلسة
  const { data: sessionData, isLoading: isLoadingSession } =
    useCurrentSession();

  // فحص إذا كانت الجلسة منتهية أو غير موجودة → توجيه إلى صفحة بدء الجلسة بدون تسجيل خروج تلقائي
  useEffect(() => {
    const sessionStarted = localStorage.getItem("sessionStarted");
    // إذا انتهى التحميل ولم توجد جلسة نشطة
    if (!isLoadingSession && !sessionData && sessionStarted) {
      toast.error("لا توجد جلسة نشطة، ابدأ جلسة جديدة");
      navigate("/start-session");
      return;
    }

    // إذا كانت الجلسة موجودة لكنها منتهية
    if (sessionData && sessionData.ended_at && sessionStarted) {
      toast.error("انتهت الجلسة، ابدأ جلسة جديدة");
      navigate("/start-session");
      return;
    }
  }, [sessionData, isLoadingSession, navigate]);

  const [page, setPage] = useState(1);
  const { data, isLoading, error, refetch, isFetching } = useInvoices(page);
  const invoices = data?.results || [];
  const showPagination = Boolean(data?.next) || Boolean(data?.previous);

  const [searchValue, setSearchValue] = useState("");
  const [filterDateRange, setFilterDateRange] = useState<
    [Dayjs | null, Dayjs | null]
  >([null, null]);
  const [filterTimeRange, setFilterTimeRange] = useState<
    [Dayjs | null, Dayjs | null]
  >([null, null]);
  const [showFilters, setShowFilters] = useState(false);

  // إعادة جلب البيانات عند العودة للصفحة
  useEffect(() => {
    // تحديث البيانات عند mount أو تغيير الصفحة
    if (!data || invoices.length === 0) {
      refetch();
    }
  }, [page]); // إزالة dependencies أخرى لتجنب infinite loop

  const toggleFilters = () => setShowFilters((prev) => !prev);

  // عرض loading إذا كان isLoading أو isFetching مع عدم وجود بيانات
  const isActuallyLoading = isLoading || (isFetching && invoices.length === 0);

  const filteredInvoices = useMemo(() => {
    return invoices.filter((invoice) => {
      const createdAt = dayjs(invoice.order?.created_at || invoice.created_at);
      const matchesSearch =
        !searchValue ||
        invoice.id.toString().includes(searchValue) ||
        (invoice.coupon_code || "")
          .toLowerCase()
          .includes(searchValue.toLowerCase()) ||
        (invoice.phone || "").includes(searchValue);

      const matchesDate =
        (!filterDateRange[0] ||
          createdAt.isSameOrAfter(filterDateRange[0], "day")) &&
        (!filterDateRange[1] ||
          createdAt.isSameOrBefore(filterDateRange[1], "day"));

      const matchesTime =
        (!filterTimeRange[0] ||
          createdAt.isSameOrAfter(filterTimeRange[0], "minute")) &&
        (!filterTimeRange[1] ||
          createdAt.isSameOrBefore(filterTimeRange[1], "minute"));

      return matchesSearch && matchesDate && matchesTime;
    });
  }, [invoices, searchValue, filterDateRange, filterTimeRange]);

  const nextPage = () => {
    if (data?.next) setPage((p) => p + 1);
  };

  const prevPage = () => {
    if (page > 1 && data?.previous) setPage((p) => p - 1);
  };

  return (
    <div className='p-4' dir='rtl'>
      <div className='flex items-center justify-between mb-4'>
        <h2 className='text-xl font-bold'>
          الفواتير {data?.count ? `(${data.count} فاتورة)` : ""}
        </h2>
      </div>

      <FilterBar
        searchValue={searchValue}
        setSearchValue={setSearchValue}
        filterDateRange={filterDateRange}
        setFilterDateRange={setFilterDateRange}
        filterTimeRange={filterTimeRange}
        setFilterTimeRange={setFilterTimeRange}
        showFilters={showFilters}
        toggleFilters={toggleFilters}
      />

      <QueryStateWrapper isLoading={isActuallyLoading} isError={!!error}>
        <div className='space-y-4 mt-4'>
          {filteredInvoices.length > 0 ? (
            filteredInvoices.map((invoice) => (
              <InvoiceCard key={invoice.id} invoice={invoice} />
            ))
          ) : (
            <div className='text-center py-8 text-gray-500'>
              {isActuallyLoading
                ? "جاري تحميل الفواتير..."
                : invoices.length === 0
                ? "لا توجد فواتير متاحة"
                : "لا توجد فواتير مطابقة للبحث"}
            </div>
          )}
        </div>

        {/* Pagination Controls */}
        {showPagination && data?.results && data.results.length > 0 && (
          <div className='flex justify-center items-center mt-6 gap-4'>
            <button
              onClick={prevPage}
              disabled={!data.previous}
              className='px-4 py-2 bg-red-100 text-red-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-red-200 transition'
            >
              السابق
            </button>

            <div className='flex items-center gap-2 text-sm text-gray-600'>
              <span>صفحة {page}</span>
              {data?.count && (
                <span>من {Math.ceil(data.count / PAGE_SIZE)}</span>
              )}
            </div>

            <button
              onClick={nextPage}
              disabled={!data.next}
              className='px-4 py-2 bg-red-100 text-red-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-red-200 transition'
            >
              التالي
            </button>
          </div>
        )}
      </QueryStateWrapper>
    </div>
  );
};

export default InvoicesPage;
